import React, { useEffect, useState } from "react";
import Axios from "../../../axios/axios";
import { Table, Card, Row, Col, Container } from "react-bootstrap";
import SupplierNavbar from "./SuppplierNavbar";
import SupplierSidebar from "./SupplierSidebar";
import "bootstrap/dist/css/bootstrap.min.css";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";

function SupplierPayments() {
  const [payments, setPayments] = useState([]);
  const [settledAmount, setSettledAmount] = useState(0);
  const [pendingAmount, setPendingAmount] = useState(0);
  const [supplierBalance, setSupplierBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [supplier, setSupplier] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [pendingCount, setPendingCount] = useState(0);
  const [settledCount, setSettledCount] = useState(0);

  useEffect(() => {
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  }, [location]);

  const supplierInfo = JSON.parse(sessionStorage.getItem("supplierInfo"));
  const supplierId = supplierInfo?.supplierId;

  useEffect(() => {
    if (!supplierId) return;

    const fetchSupplierAndPayments = async () => {
      try {
        const supplierRes = await Axios.get(`/supplier/get/${supplierId}`);
        const data = supplierRes.data?.data;
        if (data) {
          setSupplier(data);
          setSupplierBalance(data.balance || 0);
        }

        // Main transaction table data
        const paymentRes = await Axios.get(`/supplier/payments/${supplierId}`);
        setPayments(paymentRes.data.payments || []);
        setSettledAmount(paymentRes.data.settled || 0);
        setPendingAmount(paymentRes.data.pending || 0);

        // ✅ Fetch pending count
        const pendingRes = await Axios.get(
          `/supplier/pending-payments/${supplierId}`
        );
        setPendingCount(pendingRes.data.payments.length || 0);

        // ✅ Fetch settled count
        const settledRes = await Axios.get(
          `/supplier/settled-payments/${supplierId}`
        );
        setSettledCount(settledRes.data.payments.length || 0);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSupplierAndPayments();
  }, [supplierId]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "AED",
      minimumFractionDigits: 0,
    }).format(amount);

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const getStatusBadge = (status) =>
    status === "Settled" ? (
      <span className="status-badge status-settled">
        <i className="fas fa-check-circle"></i> Settled
      </span>
    ) : (
      <span className="status-badge status-pending">
        <i className="fas fa-clock"></i> Pending
      </span>
    );

  if (loading) {
    return (
      <div className="dashboard-main text-center">
        <div className="loading-spinner"></div>
        <p className="mt-3">Loading payment data...</p>
      </div>
    );
  }

  return (
    <>
      <SupplierNavbar
        supplier={supplierInfo}
        setSupplier={setSupplier}
        toggleSidebar={() => setSidebarOpen(!sidebarOpen)}
      />

      <div className="container-fluid">
        <div className="row">
          {sidebarOpen && (
            <div className="col-md-2">
              <SupplierSidebar
                closeSidebar={() => {
                  if (window.innerWidth < 768) setSidebarOpen(false);
                }}
              />
            </div>
          )}
          <div className="col-md-10">
            <div className="dashboard-main p-4">
              <Container fluid>
                <h1 className="payments-title float-animation">
                  Supplier Payments Dashboard
                </h1>

                <Row className="my-5">
                  <Col lg={4} md={6} className="mb-4">
                    <Card className="stats-card stats-card-success enhanced-card h-100">
                      <Card.Body
                        className="text-center p-4"
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/supplier/payments-list?status=Settled`)}
                      >
                        <div className="stats-icon stats-icon-success">
                          <i className="fas fa-check-circle"></i>
                        </div>
                        <Card.Title>Settled Payments</Card.Title>
                        <div className="stats-card-value pulse-animation">
                          {settledCount}
                        </div>
                        <small className="text-muted">
                          <i className="fas fa-check-circle text-success me-1"></i>{" "}
                          Paid Items
                        </small>

                        <small className="text-muted">
                          <i className="fas fa-arrow-up text-success me-1"></i>{" "}
                          Completed Payments
                        </small>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col lg={4} md={6} className="mb-4">
                    <Card className="stats-card stats-card-danger enhanced-card h-100">
                      <Card.Body
                        className="text-center p-4"
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/supplier/payments-list?status=Pending`)}
                      >
                        <div className="stats-icon stats-icon-danger">
                          <i className="fas fa-clock"></i>
                        </div>
                        <Card.Title>Pending Payments</Card.Title>
                        <div className="stats-card-value pulse-animation">
                          {pendingCount}
                        </div>
                        <small className="text-muted">
                          <i className="fas fa-hourglass-half text-warning me-1"></i>{" "}
                          Awaiting Payment
                        </small>

                        <small className="text-muted">
                          <i className="fas fa-hourglass-half text-warning me-1"></i>{" "}
                          Awaiting Settlement
                        </small>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col lg={4} md={12} className="mb-4">
                    <Card className="stats-card stats-card-info enhanced-card h-100">
                      <Card.Body className="text-center p-4">
                        <div className="stats-icon stats-icon-info">
                          <i className="fas fa-list-alt"></i>
                        </div>
                        <Card.Title>Total Transactions</Card.Title>
                        <div className="stats-card-value pulse-animation">
                          {payments.length}
                        </div>
                        <small className="text-muted">
                          <i className="fas fa-chart-line text-info me-1"></i>{" "}
                          All Time Records
                        </small>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <div className="payments-table-container">
                  <h3 className="payments-table-title">
                    <i className="fas fa-history me-2"></i>Payment Transaction
                    History
                  </h3>

                  {payments.length > 0 ? (
                    <div className="table-responsive">
                      <Table className="enhanced-table" hover>
                        <thead>
                          <tr>
                            <th>
                              <i className="fas fa-calendar-alt me-2"></i>Date
                            </th>
                            <th>
                              <i className="fas fa-hashtag me-2"></i>Order ID
                            </th>
                            <th>
                              <i className="fas fa-rupee-sign me-2"></i>Amount
                            </th>
                            <th>
                              <i className="fas fa-info-circle me-2"></i>Status
                            </th>
                            <th>
                              <i className="fas fa-credit-card me-2"></i>Payment
                              Mode
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {payments.map((payment, idx) => (
                            <tr
                              key={payment._id || idx}
                              style={{ animationDelay: `${idx * 0.1}s` }}
                            >
                              <td>
                                <strong>{formatDate(payment.date)}</strong>
                              </td>
                              <td>
                                <code className="bg-light px-2 py-1 rounded">
                                  {payment.orderId}
                                </code>
                              </td>
                              <td>
                                <span className="amount-text">
                                  {formatCurrency(payment.amount)}
                                </span>
                              </td>
                              <td>{getStatusBadge(payment.status)}</td>
                              <td>
                                <span className="text-muted">
                                  <i className="fas fa-university me-1"></i>
                                  {payment.mode || "N/A"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <div className="empty-state text-center">
                      <div className="empty-state-icon">
                        <i className="fas fa-inbox"></i>
                      </div>
                      <h4>No Payment Records Found</h4>
                      <p className="text-muted">
                        There are no payment transactions to display.
                        <br />
                        New payments will appear here once processed.
                      </p>
                    </div>
                  )}
                </div>

                {payments.length > 0 && (
                  <Row className="mt-4">
                    <Col className="text-center">
                      <div className="p-3 bg-light rounded-3">
                        <small className="text-muted">
                          <i className="fas fa-info-circle me-1"></i>
                          Showing {payments.length} transaction
                          {payments.length !== 1 ? "s" : ""} • Total Value:{" "}
                          <strong>
                            {formatCurrency(settledAmount + pendingAmount)}
                          </strong>{" "}
                          • Last Updated: {new Date().toLocaleString("en-IN")}
                        </small>
                      </div>
                    </Col>
                  </Row>
                )}
              </Container>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default SupplierPayments;
